/**
 * Order Constants
 * Matching the legacy frontend order service constants
 */

// ---------------------------------------------------------------------------
// Broker × Order-Type Compatibility
// Source of truth derived from order-service adapter analysis.
// ---------------------------------------------------------------------------

/**
 * Maps each broker name to the order types it natively supports.
 * Drives greying-out of broker rows in the order dialog when the selected
 * order type is incompatible with a given broker.
 */
export const BROKER_ORDER_TYPE_SUPPORT: Record<string, string[]> = {
  zerodha: ['market', 'limit', 'stop', 'stop_limit', 'iceberg', 'bracket'],
  groww: ['market', 'limit', 'stop', 'stop_limit'],
  upstox: ['market', 'limit', 'stop', 'stop_limit'],
  dhan: [], // stub adapter — production-blocked, no order types work
  angelone: ['market', 'limit', 'stop', 'stop_limit'],
  fyers: ['market', 'limit', 'stop', 'stop_limit'],
};

/** Returns true if the broker natively supports this order type. */
export function brokerSupportsOrderType(
  brokerName: string,
  orderType: string,
): boolean {
  const supported = BROKER_ORDER_TYPE_SUPPORT[brokerName?.toLowerCase() ?? ''] ?? [];
  return supported.includes(orderType);
}

// ---------------------------------------------------------------------------
// Order Category Groupings
// Used by the Simple / Advanced / Complex tabs in the order dialog.
// ---------------------------------------------------------------------------

export const ORDER_CATEGORY_MAP = {
  simple: ['market', 'limit', 'stop', 'stop_limit'],
  advanced: ['iceberg', 'trailing_stop', 'twap', 'vwap'],
  complex: ['bracket', 'oco'],
} as const;

export type OrderCategory = keyof typeof ORDER_CATEGORY_MAP;

// ---------------------------------------------------------------------------
// Order types that are declared but NOT yet implemented in the backend.
// These show a "NOT AVAILABLE" badge in the dialog.
// ---------------------------------------------------------------------------
export const NOT_YET_IMPLEMENTED_ORDER_TYPES = new Set([
  'trailing_stop',
  'twap',
  'vwap',
  'oco',
]);

// ---------------------------------------------------------------------------
// Order types that require a specific broker (Zerodha-only).
// These show a "ZERODHA ONLY" badge in the dialog.
// ---------------------------------------------------------------------------
export const ZERODHA_ONLY_ORDER_TYPES = new Set([
  'bracket',
  'iceberg',
]);

// ---------------------------------------------------------------------------
// TIF applicability per order type.
// Only listed values make sense for the given order type; the rest should be
// greyed-out with an explanatory tooltip in the UI.
// ---------------------------------------------------------------------------
export const ORDER_TYPE_VALID_TIF: Record<string, { values: string[]; reason: string }> = {
  market: {
    values: ['day', 'ioc'],
    reason: 'Market orders execute immediately — GTC/FOK do not apply and are silently downgraded by brokers',
  },
  limit: {
    values: ['day', 'gtc', 'ioc', 'fok'],
    reason: '',   // all valid
  },
  stop: {
    values: ['day', 'gtc'],
    reason: 'Stop orders wait in the book for a trigger — IOC/FOK are not meaningful for trigger-based orders',
  },
  stop_limit: {
    values: ['day', 'gtc'],
    reason: 'Stop-Limit orders wait for a trigger price — IOC/FOK do not apply',
  },
  iceberg: {
    values: ['day'],
    reason: 'Iceberg orders must persist through the day to drip-feed slices',
  },
  bracket: {
    values: ['day'],
    reason: 'Bracket orders are intraday and auto-exit at end of day',
  },
  trailing_stop: {
    values: ['day', 'gtc'],
    reason: 'Trailing Stop orders persist in the book — IOC/FOK do not apply',
  },
  twap: {
    values: ['day'],
    reason: 'TWAP orders spread execution across the day',
  },
  vwap: {
    values: ['day'],
    reason: 'VWAP orders spread execution across the day',
  },
  oco: {
    values: ['day', 'gtc'],
    reason: 'OCO orders persist in the book until one leg triggers',
  },
};

export const ORDER_TYPES = {
  // Basic Orders
  MARKET: 'market',
  LIMIT: 'limit',
  STOP: 'stop',
  STOP_LIMIT: 'stop_limit',

  // Advanced Stop Orders
  TRAILING_STOP: 'trailing_stop',

  // Combination Orders
  BRACKET: 'bracket',
  OCO: 'oco',
  ICEBERG: 'iceberg',

  // Post-conditions Orders
  FOK: 'fok',
  IOC: 'ioc',
  POST_ONLY: 'post_only',

  // Time-specific Orders
  MOO: 'moo',
  MOC: 'moc',
  LOO: 'loo',
  LOC: 'loc',

  // Algorithmic Orders
  TWAP: 'twap',
  VWAP: 'vwap',
  PEG: 'peg',

  // Additional Advanced Orders
  GTT: 'gtt',
  PEGGED: 'pegged',
  DISCRETIONARY: 'discretionary',
  CONDITIONAL: 'conditional',
  ALGO: 'algo',
} as const;

export const ORDER_SIDES = {
  // Basic Trading Operations
  BUY: 'buy',
  SELL: 'sell',

  // Short Selling Operations
  SHORT: 'short',
  COVER: 'cover',
  BUY_TO_COVER: 'buy_to_cover',
  SELL_SHORT: 'sell_short',

  // Position Management
  BUY_TO_OPEN: 'buy_to_open',
  SELL_TO_CLOSE: 'sell_to_close',
  SELL_TO_OPEN: 'sell_to_open',
  BUY_TO_CLOSE: 'buy_to_close',
} as const;

export const TIME_IN_FORCE = {
  DAY: 'day',
  GTC: 'gtc',
  GTD: 'gtd',
  IOC: 'ioc',
  FOK: 'fok',
  ATC: 'atc',
  ATO: 'ato',
  EXT: 'ext',
  GTD_EXT: 'gtd_ext',
  GTC_EXT: 'gtc_ext',
  DTC: 'dtc',
  GTT: 'gtt',
} as const;

/**
 * Order type options with descriptions
 */
export const ORDER_TYPE_OPTIONS = [
  // Basic Orders
  { value: ORDER_TYPES.MARKET, label: 'Market', description: 'Execute immediately at best available price' },
  { value: ORDER_TYPES.LIMIT, label: 'Limit', description: 'Execute at specified price or better' },
  { value: ORDER_TYPES.STOP, label: 'Stop', description: 'Execute when price reaches stop level' },
  { value: ORDER_TYPES.STOP_LIMIT, label: 'Stop Limit', description: 'Stop order with price limit' },

  // Advanced Stop Orders
  { value: ORDER_TYPES.TRAILING_STOP, label: 'Trailing Stop', description: 'Stop order that trails price' },

  // Combination Orders
  { value: ORDER_TYPES.BRACKET, label: 'Bracket', description: 'Entry with profit target and stop loss' },
  { value: ORDER_TYPES.OCO, label: 'OCO', description: 'One-Cancels-Other order pair' },
  { value: ORDER_TYPES.ICEBERG, label: 'Iceberg', description: 'Large order with hidden quantity' },

  // Post-conditions Orders
  { value: ORDER_TYPES.FOK, label: 'Fill or Kill', description: 'Fill entire order immediately or cancel' },
  { value: ORDER_TYPES.IOC, label: 'Immediate or Cancel', description: 'Fill as much as possible immediately' },
  { value: ORDER_TYPES.POST_ONLY, label: 'Post Only', description: 'Only add liquidity, never take' },

  // Time-specific Orders
  { value: ORDER_TYPES.MOO, label: 'Market on Open', description: 'Market order at market open' },
  { value: ORDER_TYPES.MOC, label: 'Market on Close', description: 'Market order at market close' },
  { value: ORDER_TYPES.LOO, label: 'Limit on Open', description: 'Limit order at market open' },
  { value: ORDER_TYPES.LOC, label: 'Limit on Close', description: 'Limit order at market close' },

  // Algorithmic Orders
  { value: ORDER_TYPES.TWAP, label: 'TWAP', description: 'Time-weighted average price execution' },
  { value: ORDER_TYPES.VWAP, label: 'VWAP', description: 'Volume-weighted average price execution' },
  { value: ORDER_TYPES.PEG, label: 'Pegged', description: 'Price auto-adjusts with reference price' },

  // Additional Advanced Orders
  { value: ORDER_TYPES.GTT, label: 'Good Till Triggered', description: 'Good until triggered by condition' },
  { value: ORDER_TYPES.PEGGED, label: 'Pegged Order', description: 'Pegged to another order or price' },
  { value: ORDER_TYPES.DISCRETIONARY, label: 'Discretionary', description: 'Discretionary order with offset' },
  { value: ORDER_TYPES.CONDITIONAL, label: 'Conditional', description: 'Conditional order with triggers' },
  { value: ORDER_TYPES.ALGO, label: 'Algorithmic', description: 'Algorithmic execution order' },
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Side options with descriptions
 */
export const SIDE_OPTIONS = [
  // Basic Trading Operations
  { value: ORDER_SIDES.BUY, label: 'Buy', description: 'Buy to open long position' },
  { value: ORDER_SIDES.SELL, label: 'Sell', description: 'Sell to close long position or sell short' },

  // Short Selling Operations
  { value: ORDER_SIDES.SHORT, label: 'Short', description: 'Sell short to open short position' },
  { value: ORDER_SIDES.COVER, label: 'Cover', description: 'Buy to cover short position' },
  { value: ORDER_SIDES.BUY_TO_COVER, label: 'Buy to Cover', description: 'Buy to cover short position (alias)' },
  { value: ORDER_SIDES.SELL_SHORT, label: 'Sell Short', description: 'Sell short to open short position (alias)' },

  // Position Management
  { value: ORDER_SIDES.BUY_TO_OPEN, label: 'Buy to Open', description: 'Buy to open new position' },
  { value: ORDER_SIDES.SELL_TO_CLOSE, label: 'Sell to Close', description: 'Sell to close existing position' },
  { value: ORDER_SIDES.SELL_TO_OPEN, label: 'Sell to Open', description: 'Sell to open new short position' },
  { value: ORDER_SIDES.BUY_TO_CLOSE, label: 'Buy to Close', description: 'Buy to close existing short position' },
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Time in force options
 */
export const TIME_IN_FORCE_OPTIONS = [
  { value: TIME_IN_FORCE.DAY, label: 'Day' },
  { value: TIME_IN_FORCE.GTC, label: 'Good Till Cancelled' },
  { value: TIME_IN_FORCE.IOC, label: 'Immediate Or Cancel' },
  { value: TIME_IN_FORCE.FOK, label: 'Fill Or Kill' },
  { value: TIME_IN_FORCE.GTD, label: 'Good Till Date' },
  { value: TIME_IN_FORCE.ATC, label: 'At The Close' },
  { value: TIME_IN_FORCE.ATO, label: 'At The Open' },
  { value: TIME_IN_FORCE.EXT, label: 'Extended Hours' },
  { value: TIME_IN_FORCE.GTC_EXT, label: 'GTC Extended' },
  { value: TIME_IN_FORCE.GTD_EXT, label: 'GTD Extended' },
  { value: TIME_IN_FORCE.DTC, label: 'Day Till Cancelled' },
  { value: TIME_IN_FORCE.GTT, label: 'Good Till Time' },
].sort((a, b) => a.label.localeCompare(b.label));

